import React, { useState, useEffect } from 'react';
import { QrCode as QrCodeIcon, Download, RefreshCw, Plus } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const QRCodeManagement = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const { restaurantId } = useAuth();

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    try {
      const response = await api.getTables(restaurantId);
      setTables(response.data);
    } catch (error) {
      toast.error('Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  const generateTables = async () => {
    setGenerating(true);
    try {
      await api.generateTables();
      toast.success('Tables generated successfully');
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate tables');
    } finally {
      setGenerating(false);
    }
  };

  const downloadQRCode = (tableNumber) => {
    const canvas = document.getElementById(`qr-${tableNumber}`);
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `table-${tableNumber}-qr.png`;
      link.href = url;
      link.click();
      toast.success(`QR code for Table ${tableNumber} downloaded`);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">QR Code Management</h2>
            <p className="text-gray-600 mt-1">Generate and download QR codes for each table</p>
          </div>
          <Button
            onClick={generateTables}
            disabled={generating}
            className="bg-[#E65100] hover:bg-[#D84800] text-white"
            data-testid="generate-tables-button"
          >
            {generating ? (
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Plus className="w-5 h-5 mr-2" />
            )}
            {generating ? 'Generating...' : 'Add Tables'}
          </Button>
        </div>

        {tables.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <QrCodeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No tables generated yet.</p>
            <p className="text-gray-400 text-sm">Click "Add Tables" above to generate QR codes for your tables.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {tables.map(table => (
              <div
                key={table.id}
                className="bg-white rounded-xl border border-gray-200 p-6 text-center hover:shadow-lg transition"
                data-testid={`table-qr-card-${table.table_number}`}
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">Table {table.table_number}</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-4 inline-block">
                  <QRCodeCanvas
                    id={`qr-${table.table_number}`}
                    value={`${window.location.origin}/menu/${restaurantId}/${table.table_number}`}
                    size={300}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <Button
                  onClick={() => downloadQRCode(table.table_number)}
                  variant="outline"
                  className="w-full"
                  data-testid={`download-qr-${table.table_number}-button`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download QR
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeManagement;
